// ─────────────────────────────────────────────────────────────────────────────
// Analytics Mapper Service
// Ponto ÚNICO de conversão: modelos do app → formato GA4.
// Qualquer mudança no mapeamento de dados é feita SOMENTE aqui.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { GaItem, GaItemFacets, GaPromotionParams, SuggestedListContext } from './analytics.types';
import { GA4_CURRENCY, splitBannerImageUrl, SUGGESTED_LIST } from './analytics.config';
import { Product, ScalablePrice } from '@app/models/product';
import { CartItem } from '@app/models/cart';
import { Banner } from '@app/models/banner';

/** Cache local das facetas de catálogo por item. */
const FACETS_STORAGE_KEY = 'ga4_item_facets';
const FACETS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 dias
const FACETS_MAX_ENTRIES = 500;

/** Cache local da lista de recomendação de origem por item. */
const LIST_CONTEXT_STORAGE_KEY = 'ga4_item_list_context';

interface CachedFacets {
  facets: GaItemFacets;
  saved_at: number;
}

interface CachedListContext {
  list: SuggestedListContext;
  saved_at: number;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsMapperService {

  // ────────── Product → GaItem ──────────

  /** Callback que resolve o contexto de navegação atual (categoria/marca). Setado pelo AnalyticsService. */
  catalogContextResolver: () => { category?: string; brand?: string } = () => ({});
  /** Callback que sinaliza itens no carrinho originados de Produto Sugerido. */
  suggestedCartItemResolver: (cartItem: CartItem) => boolean = () => false;
  /** Callback que reencontra o Product no catálogo a partir das chaves do item. */
  catalogProductResolver: (keys: string[]) => Product | undefined = () => undefined;

  /** Converte um Product do app para o formato GaItem do GA4. */
  productToGaItem(product: Product, quantity?: number, index?: number): GaItem {
    const facets = this.buildFacets(product, this.catalogContextResolver());
    this.rememberFacets(this.productKeys(product), facets);

    const item: GaItem = {
      item_id: product.sku || product.productId || product.id || '',
      item_name: product.name || product.label || '',
      ...facets,
      price: this.round(this.resolveProductPrice(product)),
    };

    if (typeof quantity === 'number') item.quantity = quantity;
    if (typeof index === 'number') item.index = index;

    return item;
  }

  /**
   * Facetas de catálogo do produto. Ordem: dado do próprio produto → memória do
   * add_to_cart (o carrinho devolve o produto sem marca/categoria) → `ctx`. O `ctx`
   * só complementa o que nada mais trouxe — na releitura a partir do carrinho ele
   * vem vazio, para não herdar a categoria que o usuário estava navegando.
   */
  private buildFacets(product: Product, ctx: { category?: string; brand?: string }): GaItemFacets {
    const firstSubItem = product.items?.[0];
    const remembered = this.readFacets(this.productKeys(product)) ?? {};

    const flavor = product.flavor?.label
      || product.categories?.[2]?.label;

    const facets: GaItemFacets = {
      item_brand: product.brand?.label
        || product.categories?.[1]?.label
        || firstSubItem?.label?.split(' ')?.[0]
        || remembered.item_brand
        || ctx.brand,
      item_category: product.category?.label
        || product.categories?.[0]?.label
        || remembered.item_category
        || ctx.category,
      item_variant: product.attributes?.capacityLabel
        || product.unitLabel
        || flavor
        || remembered.item_variant
        || this.buildVariantFromName(product.name || product.label),
      item_category3: product.is_redeemable ? 'Resgatável' : 'Não resgatável',
      item_category4: product.attributes?.returnable ? 'Retornável' : 'Não retornável',
      item_category5: this.buildItemTags(product),
    };

    const packaging = product.attributes?.quantityPerPackage
      ? `${product.attributes.quantityPerPackage} un`
      : remembered.item_category2;
    if (packaging) {
      facets.item_category2 = packaging;
    }

    return facets;
  }

  // ────────── Memória de catálogo do item (marca, categoria, variante, embalagem) ──────────
  // Estrutura compartilhada: o carrinho não devolve essas facetas, então quem precisa
  // delas em operações dentro do carrinho (GA4, Salesforce, ...) lê daqui.

  /** Guarda as facetas do produto no momento em que ele entra no carrinho. */
  rememberProductFacets(product: Product): void {
    const facets = this.buildFacets(product, this.catalogContextResolver());
    this.rememberFacets(this.productKeys(product), facets);
  }

  /**
   * Facetas do produto sem contexto de navegação: o que ele mesmo traz, senão a
   * memória do add_to_cart, senão a releitura do catálogo em memória.
   */
  resolveProductFacets(product: Product): GaItemFacets {
    const own = this.buildFacets(product, {});
    if (own.item_brand && own.item_category) return own;
    const fromCatalog = this.resolveItemFacets(this.productKeys(product));
    return {
      ...own,
      item_brand: own.item_brand || fromCatalog.item_brand,
      item_category: own.item_category || fromCatalog.item_category,
      item_variant: own.item_variant || fromCatalog.item_variant,
      item_category2: own.item_category2 || fromCatalog.item_category2,
    };
  }

  /** Facetas de um item do carrinho (memória do add_to_cart, senão catálogo em memória). */
  resolveCartItemFacets(cartItem: CartItem): GaItemFacets {
    return this.resolveItemFacets(this.cartItemKeys(cartItem));
  }

  /** Converte uma lista de Products para GaItems (com index sequencial). */
  productsToGaItems(products: Product[]): GaItem[] {
    return products.map((p, idx) => this.productToGaItem(p, undefined, idx));
  }

  // ────────── CartItem → GaItem ──────────

  /**
   * Converte um CartItem do app para GaItem do GA4.
   * O carrinho não devolve marca/categoria/variante: as facetas vêm do catálogo
   * (cache do add_to_cart, com fallback de busca no catálogo em memória).
   */
  cartItemToGaItem(cartItem: CartItem, index?: number, fallbackList?: SuggestedListContext): GaItem {
    const p = cartItem.product;
    const price = this.resolveProductPrice(p);
    const facets = this.resolveItemFacets(this.cartItemKeys(cartItem));

    const item: GaItem = {
      item_id: p.code || p.reference || '',
      item_name: p.name || '',
      item_brand: facets.item_brand,
      item_category: facets.item_category,
      item_variant: p.capacityLabel || facets.item_variant,
      price: this.round(price),
      quantity: cartItem.quantity ?? 1,
    };

    // Embalagem sempre em item_category2, igual ao add_to_cart (antes ia em category3).
    const packaging = p.quantityPerPackage ? `${p.quantityPerPackage} un` : facets.item_category2;
    if (packaging) item.item_category2 = packaging;
    if (facets.item_category3) item.item_category3 = facets.item_category3;
    if (facets.item_category4) item.item_category4 = facets.item_category4;
    if (facets.item_category5) item.item_category5 = facets.item_category5;

    // A lista é a memorizada no add_to_cart (com o local de exibição), não a
    // atual: no carrinho e no purchase cada item precisa manter a vitrine que
    // de fato o originou ("Produto Sugerido - Home" x "... - PDP").
    // A memória também cobre o reload da página, em que a marcação em memória do
    // CartService se perde mas o carrinho (server-side) continua lá.
    const rememberedList = this.resolveSuggestedListContext(this.cartItemKeys(cartItem));
    if (rememberedList || this.suggestedCartItemResolver(cartItem)) {
      const list = rememberedList ?? fallbackList ?? this.defaultSuggestedListContext(false);
      item.item_list_id = list.id;
      item.item_list_name = list.name;
      item.recommendation_source_item = list.source;
      item.item_origin = SUGGESTED_LIST.ITEM_ORIGIN;
    }
    if (typeof index === 'number') item.index = index;

    return item;
  }

  /**
   * Converte uma lista de CartItems para GaItems.
   * `fallbackList` cobre o item sugerido que entrou no carrinho antes desta
   * memória existir (ou em outra sessão/dispositivo).
   */
  cartItemsToGaItems(cartItems: CartItem[], fallbackList?: SuggestedListContext): GaItem[] {
    return (cartItems || []).map((ci, idx) => this.cartItemToGaItem(ci, idx, fallbackList));
  }

  /** Calcula o valor total de uma lista de CartItems. */
  cartItemsTotal(cartItems: CartItem[]): number {
    return this.round(
      (cartItems || []).reduce(
        (sum, ci) => sum + (ci.quantity ?? 1) * this.resolveProductPrice(ci.product),
        0,
      ),
    );
  }

  // ────────── Banner → GaPromotionParams ──────────

  /** Converte um Banner do app para parâmetros de evento de promoção GA4. */
  bannerToPromoParams(banner: Banner, slotIndex?: number): GaPromotionParams {
    const creativeSlot = `slot_${slotIndex ?? 0}`;
    const promotionId = String(banner.id ?? '');
    // URL do criativo dividida: domínio em promotion_name, path em creative_name.
    // O GA4 trunca a string ao gravar e a URL inteira não cabia em uma dimensão só.
    const { origin, path } = splitBannerImageUrl(banner.image);

    const items: GaItem[] = [{
      item_id: 'banner',
      item_name: 'banner',
      promotion_id: promotionId,
      promotion_name: origin,
      creative_slot: creativeSlot,
      creative_name: path,
    }];

    const params: GaPromotionParams = {
      creative_slot: creativeSlot,
      items,
    };

    if (banner.image) params.promo_img = banner.image;
    if (banner.link) params.promo_dest = banner.link;

    return params;
  }

  // ────────── Identificadores do item ──────────

  /**
   * Todos os identificadores de um produto do catálogo.
   * Catálogo devolve `sku`/`id`, carrinho devolve `code`/`reference` — guardar e
   * consultar por todas as chaves é o que faz o item se reencontrar no purchase.
   */
  productKeys(product?: Partial<Product> & { code?: string; reference?: string }): string[] {
    return this.uniqueKeys([
      product?.sku,
      product?.productId,
      product?.id,
      product?.code,
      product?.reference,
    ]);
  }

  /** Identificadores de um item do carrinho. */
  cartItemKeys(cartItem?: CartItem): string[] {
    return this.uniqueKeys([cartItem?.product?.code, cartItem?.product?.reference]);
  }

  private uniqueKeys(values: (string | undefined)[]): string[] {
    return Array.from(new Set(values.filter((v): v is string => !!v).map(String)));
  }

  // ────────── Cache de facetas de catálogo ──────────

  /** Facetas do item: cache do catálogo e, na falta, releitura do catálogo em memória. */
  private resolveItemFacets(keys: string[]): GaItemFacets {
    const cached = this.readFacets(keys);
    // Entrada gravada sem marca/categoria (releitura a partir do carrinho) ainda
    // merece uma tentativa no catálogo em memória.
    if (cached?.item_brand && cached?.item_category) return cached;

    const product = this.catalogProductResolver(keys);
    if (!product) return cached ?? {};

    // Sem contexto de navegação: no checkout ele apontaria para a última categoria vista.
    const facets = this.buildFacets(product, {});
    this.rememberFacets(this.uniqueKeys([...keys, ...this.productKeys(product)]), facets);
    return facets;
  }

  private rememberFacets(keys: string[], facets: GaItemFacets): void {
    if (!keys.length) return;
    const hasContent = Object.values(facets).some((v) => v !== undefined && v !== '');
    if (!hasContent) return;

    const cache = this.readFacetsCache();
    // Merge com o que já existe: uma releitura a partir do carrinho (sem marca/categoria)
    // não pode apagar a faceta boa gravada no add_to_cart.
    const existing = keys.map((key) => cache[key]?.facets).find(Boolean) ?? {};
    const defined = Object.fromEntries(
      Object.entries(facets).filter(([, v]) => v !== undefined && v !== ''),
    ) as GaItemFacets;
    const entry: CachedFacets = { facets: { ...existing, ...defined }, saved_at: Date.now() };
    keys.forEach((key) => cache[key] = entry);
    this.writeFacetsCache(cache);
  }

  private readFacets(keys: string[]): GaItemFacets | undefined {
    if (!keys.length) return undefined;
    const cache = this.readFacetsCache();
    for (const key of keys) {
      const entry = cache[key];
      if (entry?.facets) return entry.facets;
    }
    return undefined;
  }

  private readFacetsCache(): Record<string, CachedFacets> {
    try {
      const raw = localStorage.getItem(FACETS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, CachedFacets>;
      const out: Record<string, CachedFacets> = {};
      for (const [key, entry] of Object.entries(parsed ?? {})) {
        if (entry?.facets && Date.now() - (entry.saved_at ?? 0) <= FACETS_TTL_MS) {
          out[key] = entry;
        }
      }
      return out;
    } catch {
      return {};
    }
  }

  private writeFacetsCache(cache: Record<string, CachedFacets>): void {
    try {
      const entries = Object.entries(cache);
      const trimmed = entries.length > FACETS_MAX_ENTRIES
        ? entries.sort((a, b) => b[1].saved_at - a[1].saved_at).slice(0, FACETS_MAX_ENTRIES)
        : entries;
      localStorage.setItem(FACETS_STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
    } catch { /* silencioso */ }
  }

  // ────────── Cache da lista de recomendação de origem ──────────

  /**
   * Guarda a lista "Produto Sugerido" de onde o item saiu, no momento em que
   * ele entra no carrinho. O carrinho não devolve nada de lista: sem isso, o
   * view_cart/purchase não conseguiria dizer de qual vitrine (nem de qual
   * versão do recomendador) veio cada item vendido.
   */
  rememberSuggestedListContext(keys: string[], list: SuggestedListContext): void {
    if (!keys.length || !list?.id) return;

    const cache = this.readListContextCache();
    const entry: CachedListContext = { list, saved_at: Date.now() };
    keys.forEach((key) => cache[key] = entry);
    this.writeListContextCache(cache);
  }

  /** Lista memorizada para o item, se houver. */
  private resolveSuggestedListContext(keys: string[]): SuggestedListContext | undefined {
    if (!keys.length) return undefined;
    const cache = this.readListContextCache();
    for (const key of keys) {
      const entry = cache[key];
      if (entry?.list?.id) return entry.list;
    }
    return undefined;
  }

  /**
   * Esquece a lista dos itens comprados. Sem isso a próxima compra da mesma
   * sessão herdaria a mesma vitrine e o funil do recomendador infla.
   */
  forgetSuggestedListContexts(keysList: string[][]): void {
    const cache = this.readListContextCache();
    let changed = false;
    keysList.forEach((keys) => keys.forEach((key) => {
      if (cache[key]) {
        delete cache[key];
        changed = true;
      }
    }));
    if (changed) this.writeListContextCache(cache);
  }

  /** Lista base (sem local de exibição) — usada só como último recurso. */
  defaultSuggestedListContext(isPointsProgram: boolean): SuggestedListContext {
    return {
      id: isPointsProgram ? SUGGESTED_LIST.POINTS_ID : SUGGESTED_LIST.ID,
      name: isPointsProgram ? SUGGESTED_LIST.POINTS_NAME : SUGGESTED_LIST.NAME,
      source: SUGGESTED_LIST.SOURCE,
    };
  }

  private readListContextCache(): Record<string, CachedListContext> {
    try {
      const raw = localStorage.getItem(LIST_CONTEXT_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, CachedListContext>;
      const out: Record<string, CachedListContext> = {};
      for (const [key, entry] of Object.entries(parsed ?? {})) {
        if (entry?.list?.id && Date.now() - (entry.saved_at ?? 0) <= FACETS_TTL_MS) {
          out[key] = entry;
        }
      }
      return out;
    } catch {
      return {};
    }
  }

  private writeListContextCache(cache: Record<string, CachedListContext>): void {
    try {
      const entries = Object.entries(cache);
      const trimmed = entries.length > FACETS_MAX_ENTRIES
        ? entries.sort((a, b) => b[1].saved_at - a[1].saved_at).slice(0, FACETS_MAX_ENTRIES)
        : entries;
      localStorage.setItem(LIST_CONTEXT_STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
    } catch { /* silencioso */ }
  }

  // ────────── Helpers ──────────

  /** Extrai uma variante do nome do produto quando não há dados estruturados. */
  private buildVariantFromName(name?: string): string | undefined {
    if (!name) return undefined;
    // Tenta extrair volume/capacidade do nome (ex: "290ml", "1l", "1,5l", "2l")
    const match = name.match(/(\d+[.,]?\d*\s?(?:ml|l|L|ML))/i);
    return match?.[1] ?? undefined;
  }

  /** Monta tags descritivas do produto (combo, kit, escalonável). */
  private buildItemTags(product: Product): string {
    const tags: string[] = [];
    if (product.isCombo) tags.push('Combo');
    if (product.isKit) tags.push('Kit');
    if (product.attributes?.scalable) tags.push('Escalonável');
    return tags.length > 0 ? tags.join(', ') : 'Padrão';
  }

  /**
   * Preço do produto/pacote para GA4, não o valor por unidade.
   * Zeros da API não devem bloquear fallback para faixa escalonada.
   */
  private resolveProductPrice(product?: {
    price?: number;
    unitPrice?: number;
    currentScalable?: ScalablePrice;
    scalablePrices?: ScalablePrice[];
  }): number {
    const activeScalable = product?.currentScalable
      ?? product?.scalablePrices?.find((s) => s.active);

    const candidates = [
      product?.price,
      activeScalable?.price,
      product?.scalablePrices?.[0]?.price,
      product?.unitPrice,
      activeScalable?.unitPrice,
      product?.scalablePrices?.[0]?.unitPrice,
    ];

    const rawPrice = candidates.find((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0;
    });

    return this.normalizeCentavos(Number(rawPrice ?? 0));
  }

  /**
   * Normaliza preço que pode vir em centavos (ex: 2673) para reais (26.73).
   * Heurística: se o valor > 1000, assume centavos e divide por 100.
   */
  normalizeCentavos(value: number): number {
    if (!Number.isFinite(value) || value === 0) return 0;
    return value > 1000 ? value / 100 : value;
  }

  /** Arredonda para 2 casas decimais (padrão GA4 para valores monetários). */
  round(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
  }

  /** Remove propriedades undefined/null de um objeto (Firebase rejeita undefined). */
  sanitizeParams(params: Record<string, any>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'number' && !Number.isFinite(v)) continue;
      out[k] = v;
    }
    return out;
  }

  /** Remove propriedades undefined de um GaItem. */
  sanitizeGaItem(item: GaItem): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(item)) {
      if (v !== undefined && v !== null) {
        out[k] = typeof v === 'number' ? this.round(v) : String(v);
      }
    }
    return out;
  }

  /** Moeda padrão do projeto. */
  get currency(): string {
    return GA4_CURRENCY;
  }
}
